// Teacher feedback tickets (D-101): limits, screenshot checks and retention shared by the Worker,
// its tests and the dashboard. Screenshots are private R2 objects served only through
// /api/feedback/attachments/:id to the ticket's teacher or to support staff.
import { msg } from "./i18n/translate";

export type TicketStatus = "open" | "answered" | "resolved";
export const TICKET_STATUSES: readonly TicketStatus[] = ["open", "answered", "resolved"];
export const isTicketStatus = (value: unknown): value is TicketStatus =>
  typeof value === "string" && (TICKET_STATUSES as readonly string[]).includes(value);

/** Category values stay the Chinese keys; the dashboard shows t(value). */
export const TEACHER_FEEDBACK_CATEGORIES = [msg("使用問題"), msg("功能建議"), msg("錯誤回報"), msg("其他回饋")] as const;

export const SCREENSHOT_LIMITS = {
  perMessage: 3,
  bytes: 5 * 1024 * 1024,
} as const;
export const SCREENSHOT_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export type ScreenshotType = (typeof SCREENSHOT_TYPES)[number];
export const MESSAGE_MAX_CHARS = 3_000;
/** Text fields plus three full screenshots and multipart overhead. */
export const FEEDBACK_MAX_BODY_BYTES = SCREENSHOT_LIMITS.perMessage * SCREENSHOT_LIMITS.bytes + 64 * 1024;

/** The real image type from the first bytes; the browser's file type is not trusted. */
export function sniffScreenshot(bytes: Uint8Array): ScreenshotType | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  if (bytes.length >= 8 && [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((value, index) => bytes[index] === value)) return "image/png";
  if (bytes.length >= 12 && String.fromCharCode(...bytes.slice(0, 4)) === "RIFF" && String.fromCharCode(...bytes.slice(8, 12)) === "WEBP") return "image/webp";
  return null;
}

/** Support staff are signed-in ClassMori accounts whose email is in SUPPORT_STAFF_EMAILS (comma separated). */
export const staffEmails = (value: string | undefined) =>
  new Set((value ?? "").split(/[\s,;]+/).map((item) => item.trim().toLowerCase()).filter((item) => item.includes("@")));

/**
 * Screenshots are kept as long as the ticket (2026-10-07, D-110): purge_after stays at this far date
 * and only an account or class deletion (src/data-deletion.ts) brings it forward to now, after which
 * the scheduled purge removes the R2 object.
 */
export const KEEP_SCREENSHOT_UNTIL = "9999-12-31 23:59:59";

/** R2 objects under this prefix with no live attachment row are deleted after this grace (an upload in flight). */
export const ORPHAN_GRACE_MS = 60 * 60 * 1000;
export const ATTACHMENT_PREFIX = "feedback/";

export const attachmentKey = (workspaceId: string, ticketId: string, attachmentId: string) =>
  `feedback/${workspaceId}/${ticketId}/${attachmentId}`;
